import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts, useCategories, useUpdateStock, useDeleteProduct, useUpdateProduct, useUpdateCategory, useBulkUpdatePricesByIds, useBulkUpdateMinimumStock, usePreviewBulkUpdatePricesByIds, useRecalculateListPrices, useRecalculateCategoryPrices } from '@/hooks/useProducts';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import AsignarCodigoModal from '@/components/AsignarCodigoModal';
import { Product, ProductCategory } from '@/types';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { Plus, Search, Edit, Package, AlertTriangle, Trash2, Calculator, History, Settings, TrendingUp, CheckSquare, Square, RefreshCw, Upload, Download, Warehouse, Tag, ArrowUp, ArrowDown, ArrowUpDown, Scissors, FileText, Camera } from 'lucide-react';
import ProductImportModal from '@/components/ProductImportModal';
import LabelPrintModal from '@/components/LabelPrintModal';
import toast from 'react-hot-toast';
import { generatePriceList } from '@/services/pdfService';
import { useQueryClient } from '@tanstack/react-query';
import { productsService } from '@/services/products';
import clsx from 'clsx';
import ProductForm from '@/components/ProductForm';
import CostHistoryPanel from '@/components/CostHistoryPanel';
import CategoryFormModal from '@/components/CategoryFormModal';
import { formatPrice, formatDateShort, formatARNumber } from '@/utils/format';
import {
  Unit,
  formatStock,
  formatPricePerUnit,
  pricePerDisplayFromBase,
  pricePerBaseFromDisplay,
  toBase,
} from '@/utils/units';

// Funciones para calcular precios sugeridos
const calculateSalePrice = (costPrice: number): number => {
  const doubled = costPrice * 2;
  return Math.ceil(doubled / 100) * 100;
};

const calculateListPrice = (salePrice: number, roundTo: number = 100): number => {
  const rawPrice = salePrice * 1.10;
  return Math.ceil(rawPrice / roundTo) * roundTo;
};

// Costo FINAL por base_unit (con IVA + costos compuestos), igual que el "Costo final" del form.
const landedCostOf = (p: Product): number | null => {
  if (p.cost_price == null) return null;
  const iva = p.iva_rate != null ? Number(p.iva_rate) : 0;
  return Number(p.cost_price) * (1 + iva / 100)
    + Number(p.freight_cost || 0)
    + Number(p.tax_cost || 0)
    + Number(p.commission_cost || 0);
};

export default function Products() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [showLowStock, setShowLowStock] = useState(searchParams.get('low_stock') === 'true');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  // Producto encontrado al escanear con la pistola (ofrece Editar / Actualizar stock)
  const [scannedProduct, setScannedProduct] = useState<Product | null>(null);
  // Codigo escaneado que no coincidio con ningun producto
  const [codigoSinAsignar, setCodigoSinAsignar] = useState<string | null>(null);
  // Codigo con el que arranca un alta nueva (viene de escanear uno inexistente)
  const [codigoParaAlta, setCodigoParaAlta] = useState<string>('');

  // Permisos de escritura
  const { user, tenant } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canWrite = isAdmin || (user?.effective_permissions?.includes('products_write') ?? false);
  // Imprimir etiquetas: admin, quien pueda escribir, y también el empleado
  const canPrintLabels = canWrite || user?.role === 'employee';
  const canEditPrices = isAdmin || (user?.effective_permissions?.includes('products_edit_prices') ?? false);
  // El empleado no tiene ABM pero sí maneja stock: puede usar la pistola para
  // cargar stock y para asignarle un código a un producto.
  const canInventory = isAdmin || (user?.effective_permissions?.includes('inventory') ?? false);
  // El backend redacta cost_price/freight/tax/commission cuando el usuario no tiene products_view_costs.
  // Además ocultamos por completo la columna Costo cuando no puede verlos (ej. rol Empleado).
  const canViewCosts = isAdmin || (user?.effective_permissions?.includes('products_view_costs') ?? false);
  const [stockModal, setStockModal] = useState<{
    product: Product;
    quantity: number;
    newCost: string;
    newSalePrice: string;
    newListPrice: string;
    updatePrices: boolean;
  } | null>(null);
  const [deleteModal, setDeleteModal] = useState<Product | null>(null);
  const [historyModal, setHistoryModal] = useState<Product | null>(null);
  const [categoryModal, setCategoryModal] = useState<ProductCategory | null>(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // Estado para selección múltiple y actualización masiva
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showMinStockModal, setShowMinStockModal] = useState(false);
  const [minStockValue, setMinStockValue] = useState('');
  const bulkMinStock = useBulkUpdateMinimumStock();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);

  const { data: products, isLoading } = useProducts({
    search: search || undefined,
    category_id: selectedCategory ? Number(selectedCategory) : undefined,
    supplier_id: selectedSupplier ? Number(selectedSupplier) : undefined,
    only_low_stock: showLowStock,
    is_combo: false,  // Los combos se gestionan en su propia sección
  });
  const { data: categories } = useCategories();
  const { data: suppliers } = useSuppliers();
  const updateStock = useUpdateStock();

  // Pistola de código de barras: usamos el catálogo COMPLETO (sin los filtros de la
  // pantalla) para resolver el escaneo aunque haya una búsqueda o categoría activa.
  const { data: scanCatalog = [] } = useProducts({ only_active: true });
  // Se apaga con el ABM abierto: ahí el escaneo lo toma el formulario para
  // cargar el código del producto que se está editando. Si siguiera activo,
  // avisaría "código sin producto asignado" justo cuando el usuario está dando
  // de alta ese código.
  useBarcodeScanner({
    enabled: (canWrite || canInventory) && !isModalOpen,
    onScan: (code) => {
      const match = scanCatalog.find((p) => (p.code || '').trim() === code);
      if (match) {
        setScannedProduct(match);
      } else {
        // No existe: en vez de un "no encontrado" sin salida, se resuelve en el
        // momento (asignárselo a un producto o dar de alta uno con ese código).
        setCodigoSinAsignar(code);
      }
    },
  });
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const updateCategory = useUpdateCategory();
  const bulkUpdatePrices = useBulkUpdatePricesByIds();
  const recalculateListPrices = useRecalculateListPrices();
  const recalculateCategoryPrices = useRecalculateCategoryPrices();

  const handleStockUpdate = async () => {
    if (!stockModal) return;
    const dispUnit: Unit = (stockModal.product.display_unit as Unit) || 'unit';
    // El usuario ingresa precios en display_unit y stock en display_unit
    const convertPrice = (v?: string) => {
      if (!v) return undefined;
      const num = parseFloat(v);
      if (isNaN(num)) return undefined;
      return pricePerBaseFromDisplay(num, dispUnit);
    };
    const quantityBase = toBase(stockModal.quantity, dispUnit);
    try {
      // El empleado no tiene products_write (el PUT del producto le daría 403),
      // pero sí `inventory`: para él va por el endpoint de stock, que es lo único
      // que necesita cargar mercadería.
      if (!canWrite) {
        await updateStock.mutateAsync({
          id: stockModal.product.id,
          quantity: Math.round(quantityBase),
        });
        setStockModal(null);
        return;
      }
      // Usar updateProduct para ambos casos (con o sin cambio de precios)
      // Esto permite registrar el historial de costos siempre
      if (stockModal.updatePrices && stockModal.newCost) {
        // Con actualización de precios
        await updateProduct.mutateAsync({
          id: stockModal.product.id,
          data: {
            cost_price: convertPrice(stockModal.newCost),
            sale_price: convertPrice(stockModal.newSalePrice),
            list_price: convertPrice(stockModal.newListPrice),
          },
          options: {
            quantity_purchased: quantityBase,
          },
        });
      } else {
        // Sin cambio de precios, pero registrar el ingreso de stock en historial
        await updateProduct.mutateAsync({
          id: stockModal.product.id,
          data: {}, // Sin cambios de precio
          options: {
            quantity_purchased: quantityBase,
          },
        });
      }

      setStockModal(null);
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deleteModal) return;
    try {
      await deleteProduct.mutateAsync(deleteModal.id);
      setDeleteModal(null);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedProducts);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    const results = await Promise.allSettled(ids.map((id) => deleteProduct.mutateAsync(id)));
    setBulkDeleting(false);
    setShowBulkDelete(false);
    setSelectedProducts(new Set());
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      alert(`${failed} de ${ids.length} productos no se pudieron eliminar.`);
    }
  };

  const categoryOptions = [
    { value: '', label: 'Todas las categorías' },
    ...(categories?.map((c) => ({ value: c.id.toString(), label: c.name })) || []),
  ];

  const supplierOptions = [
    { value: '', label: 'Todos los proveedores' },
    ...(suppliers?.map((s) => ({ value: s.id.toString(), label: s.name })) || []),
  ];

  // Nombre de proveedor por producto (usa el objeto o lo resuelve por id)
  const supplierNameById = useMemo(() => {
    const m = new Map<number, string>();
    (suppliers || []).forEach((s) => m.set(s.id, s.name));
    return m;
  }, [suppliers]);
  const getSupplierName = (p: Product): string =>
    p.supplier?.name || (p.supplier_id ? supplierNameById.get(p.supplier_id) : '') || '';

  // Ordenamiento de la tabla (alfabético por nombre o proveedor)
  const [sortBy, setSortBy] = useState<'name' | 'supplier' | 'cost' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (col: 'name' | 'supplier' | 'cost') => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      // Costo arranca de mayor a menor; el resto alfabético ascendente
      setSortDir(col === 'cost' ? 'desc' : 'asc');
    }
  };

  const filteredProducts = useMemo(() => {
    const list = products || [];
    if (!sortBy) return list;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortBy === 'cost') {
        const ca = landedCostOf(a) ?? -Infinity;
        const cb = landedCostOf(b) ?? -Infinity;
        return (ca - cb) * dir;
      }
      const va = sortBy === 'name' ? a.name : getSupplierName(a);
      const vb = sortBy === 'name' ? b.name : getSupplierName(b);
      return va.localeCompare(vb, 'es', { sensitivity: 'base' }) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, sortBy, sortDir, supplierNameById]);

  // Limpiar selección cuando cambian los filtros
  useEffect(() => {
    setSelectedProducts(new Set());
  }, [selectedCategory, selectedSupplier, showLowStock, search]);

  // IDs de productos filtrados para el "seleccionar todo"
  const filteredProductIds = useMemo(() => {
    return filteredProducts?.map(p => p.id) || [];
  }, [filteredProducts]);

  // Cargar la foto de un producto desde la lista, sin abrir el ABM.
  // Va para CUALQUIER rol: el que tiene el producto en la mano suele ser el
  // empleado, que no puede entrar a editar el producto y por lo tanto no tenia
  // ninguna forma de cargarla.
  const queryClientFotos = useQueryClient();
  const fileInputFotoRef = useRef<HTMLInputElement>(null);
  const [productoFoto, setProductoFoto] = useState<Product | null>(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);

  const pedirFoto = (p: Product) => {
    setProductoFoto(p);
    fileInputFotoRef.current?.click();
  };

  const handleFotoElegida = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // permite volver a elegir el mismo archivo
    if (!file || !productoFoto) return;
    setSubiendoFoto(true);
    try {
      await productsService.uploadImage(productoFoto.id, file);
      await queryClientFotos.invalidateQueries({ queryKey: ['products'] });
      toast.success(`Foto cargada en ${productoFoto.name}`);
    } catch {
      toast.error('No se pudo cargar la foto');
    } finally {
      setSubiendoFoto(false);
      setProductoFoto(null);
    }
  };

  // Listado de precios en PDF para mandarle a los clientes: solo nombre y precio,
  // sin costos ni stock. Usa el catalogo COMPLETO, no el filtro de la pantalla.
  const [priceListLoading, setPriceListLoading] = useState(false);

  const handlePriceList = async () => {
    if (!tenant) return;
    setPriceListLoading(true);
    try {
      await generatePriceList(scanCatalog, tenant);
    } catch (e) {
      console.error('Error generando el listado de precios:', e);
      toast.error('No se pudo generar el listado de precios');
    } finally {
      setPriceListLoading(false);
    }
  };

  // Exporta un CSV (nombre + precio) para usar como fuente de datos en ZebraDesigner.
  // Si hay productos tildados, exporta esos; si no, el filtro/orden actual.
  const exportLabelsCsv = () => {
    const rows = selectedProducts.size
      ? (filteredProducts || []).filter((p) => selectedProducts.has(p.id))
      : (filteredProducts || []);
    if (rows.length === 0) return;

    const csvEsc = (v: string | number) => {
      const s = String(v ?? '');
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    // Extrae el gramaje/presentación del nombre (ej. "... 227 GR ..." -> "227 gr")
    // y devuelve el nombre sin ese dato, para poder usarlos por separado.
    const splitWeight = (raw: string): { name: string; weight: string } => {
      const re = /(\d+[.,]?\d*)\s?(kgs?|grs?|gramos?|g|ml|cc|lt?s?|litros?)\b/i;
      const m = raw.match(re);
      if (!m) return { name: raw.trim(), weight: '' };
      const weight = `${m[1]} ${m[2].toLowerCase()}`;
      const cleaned = raw.replace(m[0], ' ').replace(/\s{2,}/g, ' ').replace(/\s*[-·|,]\s*$/, '').trim();
      return { name: cleaned || raw.trim(), weight };
    };

    // Separador ';' → Excel en español lo abre en columnas con doble clic
    // (y ZebraDesigner deja elegir el delimitador al importar).
    const SEP = ';';
    const header = ['nombre', 'nombre_limpio', 'gramaje', 'precio', 'precio_texto', 'codigo', 'proveedor'];
    const lines = [header.join(SEP)];
    for (const p of rows) {
      const unit = (p.display_unit as Unit) || 'unit';
      // Precio en la unidad de display (por kg si es a granel, por unidad si no)
      const disp = p.sale_price == null ? null : Math.round(pricePerDisplayFromBase(Number(p.sale_price), unit));
      const precio = disp == null ? '' : disp;
      const precioTxt = disp == null ? '' : '$' + disp.toLocaleString('es-AR');
      const { name: nameClean, weight } = splitWeight(p.name);
      // Gramaje: fraccionado -> fraction_grams; si no, lo que saquemos del nombre
      const gramaje = p.source_product_id && p.fraction_grams ? `${Number(p.fraction_grams)} g` : weight;
      lines.push([
        csvEsc(p.name),
        csvEsc(nameClean),
        csvEsc(gramaje),
        csvEsc(precio),
        csvEsc(precioTxt),
        csvEsc(p.code || ''),
        csvEsc(getSupplierName(p)),
      ].join(SEP));
    }
    // BOM para que ZebraDesigner/Excel respeten acentos y ñ
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `productos-etiquetas-${rows.length}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const allSelected = filteredProductIds.length > 0 && filteredProductIds.every(id => selectedProducts.has(id));
  const someSelected = selectedProducts.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProductIds));
    }
  };

  const toggleProductSelection = (productId: number) => {
    const newSelection = new Set(selectedProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedProducts(newSelection);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Productos</h1>
          <p className="text-gray-600">Gestiona tu inventario de productos y servicios</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Listado de precios: es lo unico que sale del sistema pensado para que lo
              vea alguien de afuera, asi que no va detras de un permiso de escritura. */}
          <Button
            variant="secondary"
            onClick={handlePriceList}
            loading={priceListLoading}
            title="PDF con nombre y precio de venta, para compartir con los clientes"
          >
            <FileText className="h-4 w-4 mr-2" />
            Listado de precios
          </Button>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowCategoriesModal(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Categorías
            </Button>
            <Button variant="secondary" onClick={() => setShowImportModal(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Importar Excel
            </Button>
            <Button variant="secondary" onClick={exportLabelsCsv} title="Exporta nombre y precio para ZebraDesigner (seleccionados, o el filtro actual)">
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
            <Button onClick={() => setIsModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Producto
            </Button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar productos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 min-w-[180px]">
            <Select
              options={categoryOptions}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex-1 min-w-[150px]"
            />
            {canWrite && selectedCategory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const cat = categories?.find(c => c.id.toString() === selectedCategory);
                  if (cat) setCategoryModal(cat);
                }}
                title="Configurar multiplicador"
                className="px-2"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Select
            options={supplierOptions}
            value={selectedSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="min-w-[170px]"
          />
          <label className="flex items-center space-x-2 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showLowStock}
              onChange={(e) => setShowLowStock(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Solo stock bajo</span>
          </label>
        </div>

        {/* Barra de acciones de selección */}
        {canPrintLabels && someSelected && (
          <div className="mt-4 pt-4 border-t flex items-center justify-between gap-3 flex-wrap bg-blue-50 -mx-6 -mb-6 px-6 py-3">
            <span className="text-sm text-blue-800">
              {selectedProducts.size} producto{selectedProducts.size !== 1 ? 's' : ''} seleccionado{selectedProducts.size !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canWrite && (
                <Button variant="danger" onClick={() => setShowBulkDelete(true)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Borrar
                </Button>
              )}
              <Button variant="secondary" onClick={() => setShowLabelsModal(true)}>
                <Tag className="h-4 w-4 mr-2" />
                Etiquetas
              </Button>
              {canWrite && (
                <Button variant="secondary" onClick={() => { setMinStockValue(''); setShowMinStockModal(true); }}>
                  <Warehouse className="h-4 w-4 mr-2" />
                  Stock mínimo
                </Button>
              )}
              {canEditPrices && (
                <Button onClick={() => setShowBulkUpdateModal(true)}>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Actualizar Precios
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lista de productos */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : filteredProducts && filteredProducts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canPrintLabels && (
                    <th className="px-4 py-3 text-left">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-500 hover:text-gray-700"
                        title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                      >
                        {allSelected ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <button onClick={() => toggleSort('name')} className="flex items-center gap-1 uppercase hover:text-gray-700">
                      Producto
                      {sortBy === 'name' ? (sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 text-gray-300" />}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <button onClick={() => toggleSort('supplier')} className="flex items-center gap-1 uppercase hover:text-gray-700">
                      Proveedor
                      {sortBy === 'supplier' ? (sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 text-gray-300" />}
                    </button>
                  </th>
                  {canViewCosts && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <button onClick={() => toggleSort('cost')} className="flex items-center gap-1 uppercase hover:text-gray-700 ml-auto">
                        Costo
                        {sortBy === 'cost' ? (sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 text-gray-300" />}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Vencimiento
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => {
                  const isLowStock = product.track_stock && Number(product.current_stock) <= Number(product.minimum_stock);
                  const isSelected = selectedProducts.has(product.id);
                  return (
                    <tr
                      key={product.id}
                      className={clsx('hover:bg-gray-50', canWrite && 'cursor-pointer', isSelected && 'bg-blue-50')}
                      onClick={canWrite ? () => {
                        setEditingProduct(product);
                        setIsModalOpen(true);
                      } : undefined}
                    >
                      {canPrintLabels && (
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleProductSelection(product.id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-primary-600" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                              {product.source_product_id && (
                                <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0">
                                  FRACCIONADO
                                </span>
                              )}
                              <span>{product.name}</span>
                            </div>
                            {product.source_product_id && product.source_product_name ? (
                              <div className="text-xs text-teal-600">
                                de {product.source_product_name}
                                {product.fraction_grams ? ` · ${Number(product.fraction_grams)}g` : ''}
                              </div>
                            ) : null}
                            {product.code && (
                              <div className="text-sm text-gray-500">{product.code}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getSupplierName(product) || '-'}
                      </td>
                      {canViewCosts && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-700">
                          {product.cost_price != null
                            ? formatPricePerUnit(landedCostOf(product), (product.display_unit as Unit) || 'unit', product.dimension === 'weight')
                            : <span className="text-gray-400">-</span>}
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                        {product.sale_price != null
                          ? formatPricePerUnit(product.sale_price, (product.display_unit as Unit) || 'unit', product.dimension === 'weight')
                          : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {product.track_stock ? (
                          canWrite ? (
                            <button
                              onClick={() => {
                                const dispUnit: Unit = (product.display_unit as Unit) || 'unit';
                                const toDisplayPrice = (v?: number | null) =>
                                  v === null || v === undefined ? '' : pricePerDisplayFromBase(Number(v), dispUnit).toString();
                                setStockModal({
                                  product,
                                  quantity: 0,
                                  newCost: toDisplayPrice(product.cost_price),
                                  newSalePrice: toDisplayPrice(product.sale_price),
                                  newListPrice: toDisplayPrice(product.list_price),
                                  updatePrices: false,
                                });
                              }}
                              className={clsx(
                                'inline-flex items-center px-2 py-1 rounded text-sm',
                                isLowStock
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                              )}
                            >
                              {isLowStock && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {formatStock(Number(product.current_stock), (product.display_unit as Unit) || 'unit')}
                            </button>
                          ) : (
                            <span
                              className={clsx(
                                'inline-flex items-center px-2 py-1 rounded text-sm',
                                isLowStock
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                              )}
                            >
                              {isLowStock && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {formatStock(Number(product.current_stock), (product.display_unit as Unit) || 'unit')}
                            </span>
                          )
                        ) : product.source_product_id ? (
                          // Fraccionado: no tiene stock propio. Lo que se muestra es el
                          // stock del granel padre, compartido con los demas paquetes que
                          // salen de el. Se marca explicito para que nadie intente cargarlo aca.
                          <div className="inline-flex flex-col items-start gap-0.5">
                            <span
                              className={clsx(
                                'inline-flex items-center px-2 py-1 rounded text-sm',
                                Number(product.source_current_stock ?? 0) <= 0
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-teal-100 text-teal-800'
                              )}
                              title={`Stock del granel${product.source_product_name ? ` "${product.source_product_name}"` : ''}, compartido entre todos sus fraccionados. Se carga en el granel, no aca.`}
                            >
                              <Scissors className="h-3 w-3 mr-1 flex-shrink-0" />
                              {formatARNumber(Number(product.source_current_stock ?? 0) / 1000)} kg
                            </span>
                            <span
                              className="text-[11px] text-gray-500 max-w-[150px] truncate"
                              title={product.source_product_name ?? ''}
                            >
                              del granel{product.source_product_name ? `: ${product.source_product_name}` : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.expiration_date ? formatDateShort(product.expiration_date) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {/* Foto: sin permiso de escritura, para que la pueda
                                cargar quien tiene el producto en la mano. */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => pedirFoto(product)}
                              disabled={subiendoFoto}
                              title={product.image_url ? 'Cambiar la foto' : 'Cargar una foto'}
                              className={product.image_url
                                ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                                : 'text-gray-400 hover:text-gray-600'}
                            >
                              <Camera className="h-4 w-4" />
                            </Button>
                            {canWrite && (<>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHistoryModal(product)}
                              title="Ver historial de costos"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingProduct(product);
                                setIsModalOpen(true);
                              }}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteModal(product)}
                              title="Eliminar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            </>)}
                          </div>
                        </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No se encontraron productos
          </div>
        )}
      </div>

      {/* Modal de producto */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProduct(null);
        }}
        title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        size="lg"
      >
        <div className="space-y-4">
          <ProductForm
            product={editingProduct}
            initialCode={codigoParaAlta}
            onSuccess={() => {
              setIsModalOpen(false);
              setEditingProduct(null);
              setCodigoParaAlta('');
            }}
          />
          {/* Historial de costos (solo en edición) */}
          {editingProduct && (
            <div className="border-t pt-4">
              <CostHistoryPanel
                productId={editingProduct.id}
                productName={editingProduct.name}
                displayUnit={(editingProduct.display_unit as any) || 'unit'}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Modal de stock */}
      <Modal
        isOpen={!!stockModal}
        onClose={() => setStockModal(null)}
        title="Ajustar Stock"
        size="md"
      >
        {stockModal && (
          <StockUpdateModal
            stockModal={stockModal}
            setStockModal={setStockModal}
            onUpdate={handleStockUpdate}
            isLoading={updateStock.isPending || updateProduct.isPending}
            canWrite={canWrite}
          />
        )}
      </Modal>

      {/* Escaneaste un código que no está en ningún producto: asignarlo o dar de alta */}
      {/* Selector de archivo para la foto del producto. Uno solo para toda la
          tabla: se dispara desde el boton de la fila que corresponda. */}
      <input
        ref={fileInputFotoRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleFotoElegida}
      />

      <AsignarCodigoModal
        code={codigoSinAsignar}
        products={scanCatalog}
        onClose={() => setCodigoSinAsignar(null)}
        onCreateNew={canWrite ? (code) => {
          setEditingProduct(null);
          setCodigoParaAlta(code);
          setIsModalOpen(true);
        } : undefined}
      />

      {/* Selector al escanear con la pistola: Editar producto o Actualizar stock */}
      <Modal
        isOpen={!!scannedProduct}
        onClose={() => setScannedProduct(null)}
        title="Producto escaneado"
        size="md"
      >
        {scannedProduct && (
          <div className="space-y-4">
            {/* Ficha del producto: lo primero es reconocerlo de un vistazo y ver
                a cuánto se vende, sin tener que entrar a editarlo. */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex gap-3 p-3">
                {scannedProduct.image_url ? (
                  <img
                    src={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''}${scannedProduct.image_url}`}
                    alt={scannedProduct.name}
                    className="h-20 w-20 rounded-lg object-cover flex-shrink-0 bg-gray-50"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Package className="h-9 w-9 text-gray-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-lg leading-tight">
                    {scannedProduct.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {(typeof scannedProduct.category === 'object' && scannedProduct.category?.name)
                      || categories?.find((c) => c.id === scannedProduct.category_id)?.name
                      || 'Sin categoría'}
                  </p>
                  <p className="font-mono text-xs text-gray-400 mt-1 break-all">
                    {scannedProduct.code}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200 bg-gray-50">
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Precio de venta</p>
                  <p className="text-2xl font-bold text-emerald-700 tabular-nums leading-tight">
                    {formatPrice(pricePerDisplayFromBase(
                      Number(scannedProduct.sale_price || 0),
                      (scannedProduct.display_unit as Unit) || 'unit',
                    ))}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {formatPricePerUnit(
                      Number(scannedProduct.sale_price || 0),
                      (scannedProduct.display_unit as Unit) || 'unit',
                    )}
                  </p>
                </div>
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Stock</p>
                  {scannedProduct.track_stock ? (
                    <p className={clsx(
                      'text-2xl font-bold tabular-nums leading-tight',
                      Number(scannedProduct.current_stock) <= Number(scannedProduct.minimum_stock)
                        ? 'text-red-600' : 'text-gray-800',
                    )}>
                      {formatStock(
                        Number(scannedProduct.current_stock),
                        (scannedProduct.display_unit as Unit) || 'unit',
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1.5">No lleva stock</p>
                  )}
                  {scannedProduct.track_stock
                    && Number(scannedProduct.current_stock) <= Number(scannedProduct.minimum_stock) && (
                    <p className="text-[11px] text-red-600">Por debajo del mínimo</p>
                  )}
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600">¿Qué querés hacer?</p>
            <div className="grid grid-cols-1 gap-2">
              {canWrite && (
                <Button
                  onClick={() => {
                    setEditingProduct(scannedProduct);
                    setIsModalOpen(true);
                    setScannedProduct(null);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" /> Editar producto
                </Button>
              )}
              {scannedProduct.track_stock && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    const dispUnit: Unit = (scannedProduct.display_unit as Unit) || 'unit';
                    const toDisplayPrice = (v?: number | null) =>
                      v === null || v === undefined ? '' : pricePerDisplayFromBase(Number(v), dispUnit).toString();
                    setStockModal({
                      product: scannedProduct,
                      quantity: 0,
                      newCost: toDisplayPrice(scannedProduct.cost_price),
                      newSalePrice: toDisplayPrice(scannedProduct.sale_price),
                      newListPrice: toDisplayPrice(scannedProduct.list_price),
                      updatePrices: false,
                    });
                    setScannedProduct(null);
                  }}
                >
                  <Package className="h-4 w-4 mr-2" /> Actualizar stock
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Eliminar Producto"
        size="sm"
      >
        {deleteModal && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de que deseas eliminar <strong>{deleteModal.name}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteModal(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteProduct}
                loading={deleteProduct.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de borrado múltiple */}
      <Modal
        isOpen={showBulkDelete}
        onClose={() => setShowBulkDelete(false)}
        title="Eliminar productos"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas eliminar los <strong>{selectedProducts.size}</strong> productos seleccionados?
          </p>
          <p className="text-sm text-red-600">
            Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowBulkDelete(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleBulkDelete} loading={bulkDeleting}>
              Eliminar {selectedProducts.size}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de historial de costos */}
      <Modal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={`Historial de Costos - ${historyModal?.name || ''}`}
        size="xl"
      >
        {historyModal && (
          <CostHistoryPanel
            productId={historyModal.id}
            productName={historyModal.name}
            displayUnit={(historyModal.display_unit as any) || 'unit'}
          />
        )}
      </Modal>

      {/* Modal de configuración de categoría (formulario completo) */}
      <Modal
        isOpen={!!categoryModal}
        onClose={() => setCategoryModal(null)}
        title={`Editar Categoría: ${categoryModal?.name || ''}`}
        size="xl"
      >
        {categoryModal && (
          <CategoryFormModal
            category={categoryModal}
            onSave={async (data) => {
              await updateCategory.mutateAsync({ id: categoryModal.id, data });
            }}
            onClose={() => setCategoryModal(null)}
            isLoading={updateCategory.isPending}
            onRecalculate={async (categoryId) => {
              try {
                const result = await recalculateListPrices.mutateAsync(categoryId);
                toast.success(`Se actualizaron ${result.updated_count} productos`);
              } catch {
                toast.error('Error al recalcular precios');
              }
            }}
            isRecalculating={recalculateListPrices.isPending}
          />
        )}
      </Modal>

      {/* Modal de lista de categorías */}
      <Modal
        isOpen={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
        title="Configurar Categorías"
        size="md"
      >
        <CategoriesListModal
          categories={categories || []}
          onEditCategory={(cat) => {
            setShowCategoriesModal(false);
            setCategoryModal(cat);
          }}
          onRecalculate={async (categoryId) => {
            try {
              const result = await recalculateCategoryPrices.mutateAsync(categoryId);
              toast.success(`Se recalcularon precios de ${result.updated_count} productos`);
            } catch {
              toast.error('Error al recalcular precios');
            }
          }}
          isRecalculating={recalculateCategoryPrices.isPending}
        />
      </Modal>

      {/* Modal de actualización masiva de precios */}
      <Modal
        isOpen={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        title="Actualizar Precios Masivamente"
        size="md"
      >
        <BulkPriceUpdateModal
          selectedCount={selectedProducts.size}
          selectedProductIds={Array.from(selectedProducts)}
          products={filteredProducts || []}
          onUpdate={async (percentage, recalculateFromCost, reason) => {
            await bulkUpdatePrices.mutateAsync({
              product_ids: Array.from(selectedProducts),
              percentage,
              recalculate_from_cost: recalculateFromCost,
              reason,
            });
            setShowBulkUpdateModal(false);
            setSelectedProducts(new Set());
          }}
          onClose={() => setShowBulkUpdateModal(false)}
          isLoading={bulkUpdatePrices.isPending}
        />
      </Modal>

      <ProductImportModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} />

      <LabelPrintModal
        isOpen={showLabelsModal}
        onClose={() => setShowLabelsModal(false)}
        products={(filteredProducts || []).filter((p) => selectedProducts.has(p.id))}
      />

      {/* Modal: stock mínimo masivo */}
      <Modal
        isOpen={showMinStockModal}
        onClose={() => setShowMinStockModal(false)}
        title="Stock mínimo masivo"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Se aplicará el mismo stock mínimo a los <strong>{selectedProducts.size}</strong> productos
            seleccionados. El valor se interpreta en la unidad de cada producto (u / kg / g).
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stock mínimo</label>
            <input
              type="number"
              min="0"
              step="any"
              value={minStockValue}
              onChange={(e) => setMinStockValue(e.target.value)}
              placeholder="Ej: 10"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowMinStockModal(false)}>Cancelar</Button>
            <Button
              loading={bulkMinStock.isPending}
              disabled={minStockValue === '' || Number(minStockValue) < 0}
              onClick={async () => {
                await bulkMinStock.mutateAsync({
                  product_ids: Array.from(selectedProducts),
                  minimum_stock: Number(minStockValue),
                });
                setShowMinStockModal(false);
                setSelectedProducts(new Set());
              }}
            >
              Aplicar a {selectedProducts.size}
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

// Componente para el modal de actualización de stock con precios
interface StockModalData {
  product: Product;
  quantity: number;
  newCost: string;
  newSalePrice: string;
  newListPrice: string;
  updatePrices: boolean;
}

function StockUpdateModal({
  stockModal,
  setStockModal,
  onUpdate,
  isLoading,
  canWrite,
}: {
  stockModal: StockModalData;
  setStockModal: (data: StockModalData | null) => void;
  onUpdate: () => void;
  isLoading: boolean;
  /** Sin ABM (empleado): carga stock pero no toca precios. */
  canWrite: boolean;
}) {
  const [localCost, setLocalCost] = useState(stockModal.newCost);
  const [localSalePrice, setLocalSalePrice] = useState(stockModal.newSalePrice);
  const [localListPrice, setLocalListPrice] = useState(stockModal.newListPrice);

  // Precio sugerido cuando cambia el costo
  const suggestedSalePrice = localCost ? calculateSalePrice(parseFloat(localCost)) : null;

  // Sincronizar con el estado padre
  useEffect(() => {
    setStockModal({
      ...stockModal,
      newCost: localCost,
      newSalePrice: localSalePrice,
      newListPrice: localListPrice,
    });
  }, [localCost, localSalePrice, localListPrice]);

  const applySuggestedSalePrice = () => {
    if (suggestedSalePrice) {
      setLocalSalePrice(suggestedSalePrice.toString());
    }
  };

  const applyAllSuggested = () => {
    if (suggestedSalePrice) {
      setLocalSalePrice(suggestedSalePrice.toString());
      const newListPrice = calculateListPrice(suggestedSalePrice);
      setLocalListPrice(newListPrice.toString());
    }
  };

  const dispUnit: Unit = (stockModal.product.display_unit as Unit) || 'unit';
  const isWeight = stockModal.product.dimension === 'weight';

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 p-3 rounded-lg">
        <p className="text-gray-600">
          <strong>{stockModal.product.name}</strong>
        </p>
        <p className="text-sm text-gray-500">
          Stock actual: <span className="font-medium">{formatStock(Number(stockModal.product.current_stock), dispUnit)}</span>
          {stockModal.product.cost_price && (
            <> | Costo actual: <span className="font-medium">{formatPricePerUnit(stockModal.product.cost_price, dispUnit)}</span></>
          )}
        </p>
      </div>

      <Input
        type="number"
        step={isWeight ? '0.01' : '1'}
        label={`Cantidad a agregar/quitar (${dispUnit === 'unit' ? 'unidades' : dispUnit === 'kilogram' ? 'kg' : 'g'})`}
        value={stockModal.quantity}
        onChange={(e) =>
          setStockModal({ ...stockModal, quantity: Number(e.target.value) })
        }
        placeholder="Ej: 10 o -5"
      />
      <p className="text-xs text-gray-500">
        Usa números positivos para agregar y negativos para quitar.
        Nuevo stock: <strong>{formatStock(Number(stockModal.product.current_stock) + toBase(stockModal.quantity, dispUnit), dispUnit)}</strong>
      </p>

      {/* Checkbox para actualizar precios. Oculto para quien no puede editar el
          producto (el empleado carga stock, no toca precios). */}
      {canWrite && (
        <div className="border-t pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stockModal.updatePrices}
              onChange={(e) => setStockModal({ ...stockModal, updatePrices: e.target.checked })}
              className="rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm font-medium">Actualizar precios con nuevo costo</span>
          </label>
        </div>
      )}

      {stockModal.updatePrices && (
        <div className="bg-blue-50 p-4 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-700 flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Actualizar Precios
            </h4>
            {localCost && (
              <button
                type="button"
                onClick={applyAllSuggested}
                className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
              >
                Aplicar precios sugeridos
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="number"
                label={`Nuevo Costo${isWeight ? `/${dispUnit === 'kilogram' ? 'kg' : 'g'}` : ''}`}
                value={localCost}
                onChange={(e) => setLocalCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Input
                type="number"
                label={`Precio${isWeight ? `/${dispUnit === 'kilogram' ? 'kg' : 'g'}` : ''}`}
                value={localSalePrice}
                onChange={(e) => setLocalSalePrice(e.target.value)}
                placeholder="0.00"
              />
              {suggestedSalePrice && localSalePrice !== suggestedSalePrice.toString() && (
                <button
                  type="button"
                  onClick={applySuggestedSalePrice}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  Sugerido: ${suggestedSalePrice.toLocaleString('es-AR')}
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-600">
            Precio sugerido = Costo × multiplicador de la categoría.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={() => setStockModal(null)}>
          Cancelar
        </Button>
        <Button onClick={onUpdate} loading={isLoading}>
          {stockModal.updatePrices ? 'Actualizar Stock y Precios' : 'Actualizar Stock'}
        </Button>
      </div>
    </div>
  );
}

// Componente para listar categorías y sus multiplicadores
function CategoriesListModal({
  categories,
  onEditCategory,
  onRecalculate,
  isRecalculating,
}: {
  categories: ProductCategory[];
  onEditCategory: (category: ProductCategory) => void;
  onRecalculate: (categoryId: number) => void;
  isRecalculating: boolean;
}) {
  // Obtener descripción de la fórmula de efectivo (lee de los campos NUEVOS)
  const getFormulaDescription = (category: ProductCategory): string => {
    const method = category.pricing_method || 'multiplier';
    const cashVal = Number(category.cash_multiplier ?? 0);
    if (method === 'margin') {
      return `Margen ${cashVal}%`;
    }
    return `×${cashVal}`;
  };

  if (categories.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay categorías creadas
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600 mb-4">
        Configurá la fórmula de precio para cada categoría
      </p>
      <div className="divide-y">
        {categories.map((category) => (
          <div
            key={category.id}
            className="flex items-center justify-between py-3 hover:bg-gray-50 px-2 rounded"
          >
            <div className="cursor-pointer flex-1" onClick={() => onEditCategory(category)}>
              <p className="font-medium text-gray-900">{category.name}</p>
              <p className="text-sm text-gray-500">
                Precio: {getFormulaDescription(category)}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRecalculate(category.id)}
                disabled={isRecalculating}
                title="Recalcular los 4 precios de venta desde el costo, usando la fórmula de esta categoría (sobreescribe precios manuales)"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEditCategory(category)}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Componente para actualización masiva de precios
function BulkPriceUpdateModal({
  selectedCount,
  selectedProductIds,
  products,
  onUpdate,
  onClose,
  isLoading,
}: {
  selectedCount: number;
  selectedProductIds: number[];
  products: Product[];
  onUpdate: (percentage: number, recalculateFromCost: boolean, reason?: string) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}) {
  const [percentage, setPercentage] = useState<string>('10');
  const [recalculateFromCost, setRecalculateFromCost] = useState(true);
  const [reason, setReason] = useState('');
  const previewMutation = usePreviewBulkUpdatePricesByIds();
  const serverPreview = previewMutation.data;
  const previewLoading = previewMutation.isPending;
  // Cuando cambian los inputs, descartar el preview del servidor
  useEffect(() => {
    previewMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentage, recalculateFromCost]);

  // Preview de algunos productos seleccionados
  const previewProducts = useMemo(() => {
    return products
      .filter(p => selectedProductIds.includes(p.id))
      .slice(0, 5);
  }, [products, selectedProductIds]);

  const percentageValue = parseFloat(percentage) || 0;

  // Calcular nuevos precios según la configuración
  const calculateNewPrices = (product: Product) => {
    const costPrice = product.cost_price || 0;
    if (!costPrice) return { salePrice: null, listPrice: null };

    // Nuevo costo con el porcentaje aplicado
    const newCost = costPrice * (1 + percentageValue / 100);

    if (recalculateFromCost && product.category) {
      // Usar fórmula de categoría
      const formula = product.category.price_formula || 'multiply_round_up';
      const multiplier = product.category.price_multiplier || 2.0;
      const marginPct = product.category.margin_percentage || 30;
      const roundTo = product.category.price_round_to || 100;
      const listMultiplier = product.category.list_price_multiplier || 1.10;

      let newSalePrice: number;
      if (formula === 'margin_percentage') {
        // Costo + margen%
        newSalePrice = Math.ceil((newCost * (1 + marginPct / 100)) / roundTo) * roundTo;
      } else if (formula === 'multiply_round_up') {
        newSalePrice = Math.ceil((newCost * multiplier) / roundTo) * roundTo;
      } else if (formula === 'multiply_exact') {
        newSalePrice = Math.round(newCost * multiplier * 100) / 100;
      } else if (formula === 'fixed_margin') {
        newSalePrice = Math.round((newCost + multiplier) * 100) / 100;
      } else {
        newSalePrice = Math.ceil((newCost * 2) / 100) * 100;
      }

      // Precio tarjeta redondeado hacia arriba
      const newListPrice = Math.ceil((newSalePrice * listMultiplier) / roundTo) * roundTo;

      return { salePrice: newSalePrice, listPrice: newListPrice };
    } else {
      // Aplicar porcentaje directo sobre precio actual
      const salePrice = product.sale_price || 0;
      const listPrice = product.list_price || 0;
      return {
        salePrice: salePrice ? Math.round(salePrice * (1 + percentageValue / 100)) : null,
        listPrice: listPrice ? Math.round(listPrice * (1 + percentageValue / 100)) : null,
      };
    }
  };

  const handleFetchPreview = async () => {
    await previewMutation.mutateAsync({
      product_ids: selectedProductIds,
      percentage: percentageValue,
      recalculate_from_cost: recalculateFromCost,
      reason: reason || undefined,
    });
  };

  const handleSubmit = async () => {
    await onUpdate(percentageValue, recalculateFromCost, reason || undefined);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          Se actualizarán los precios de <strong>{selectedCount} producto{selectedCount !== 1 ? 's' : ''}</strong>
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Porcentaje de ajuste
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              className="w-32"
              placeholder="10"
            />
            <span className="text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Usa valores negativos para reducir precios (ej: -10 para 10% menos)
          </p>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={recalculateFromCost}
              onChange={(e) => setRecalculateFromCost(e.target.checked)}
              className="rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm">Recalcular usando fórmula de categoría</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Si está marcado, primero aumenta el costo y luego recalcula el precio según la fórmula de la categoría.
            Si no, aplica el porcentaje directamente sobre el precio actual.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo (opcional)
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: Aumento de proveedor"
          />
        </div>
      </div>

      {/* Preview */}
      {previewProducts.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 flex justify-between">
            <span>Vista previa (estimado)</span>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>Precio</span>
            </div>
          </div>
          <div className="divide-y max-h-48 overflow-y-auto">
            {previewProducts.map(product => {
              const { salePrice } = calculateNewPrices(product);
              return (
                <div key={product.id} className="px-4 py-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 truncate flex-1 mr-2">{product.name}</span>
                    <div className="flex gap-4 text-right">
                      <div className="w-24">
                        <span className="text-gray-400 line-through text-xs block">
                          {formatPrice(product.sale_price)}
                        </span>
                        <span className="text-gray-900 font-medium">
                          {salePrice ? formatPrice(salePrice) : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {selectedCount > 5 && (
              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                ... y {selectedCount - 5} más
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vista previa exacta del servidor */}
      {serverPreview && (
        <div className="border-2 border-emerald-300 rounded-lg overflow-hidden">
          <div className="bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-900 flex justify-between items-center">
            <span>
              ✓ Preview servidor: <strong>{serverPreview.will_update}</strong> actualizar
              {serverPreview.will_skip > 0 && (
                <span className="text-amber-700 ml-2">· {serverPreview.will_skip} omitir</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => previewMutation.reset()}
              className="text-xs text-emerald-700 hover:underline"
            >
              Recalcular
            </button>
          </div>
          <div className="divide-y max-h-64 overflow-y-auto bg-white">
            {serverPreview.items.map((it) => (
              <div key={it.product_id} className="px-4 py-2 text-sm">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-700 truncate flex-1">{it.product_name}</span>
                  {it.skipped_reason ? (
                    <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                      {it.skipped_reason}
                    </span>
                  ) : (
                    <div className="flex gap-3 text-right text-xs">
                      <div className="w-24">
                        <p className="text-gray-400">Costo</p>
                        <p>
                          <span className="line-through text-gray-400">
                            {it.old_cost != null ? formatPrice(Number(it.old_cost)) : '—'}
                          </span>{' '}
                          <span className="text-gray-900 font-medium">
                            {it.new_cost != null ? formatPrice(Number(it.new_cost)) : '—'}
                          </span>
                        </p>
                      </div>
                      <div className="w-24">
                        <p className="text-gray-400">Precio</p>
                        <p>
                          <span className="line-through text-gray-400">
                            {it.old_sale_price != null ? formatPrice(Number(it.old_sale_price)) : '—'}
                          </span>{' '}
                          <span className="text-emerald-700 font-medium">
                            {it.new_sale_price != null ? formatPrice(Number(it.new_sale_price)) : '—'}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        {!serverPreview ? (
          <Button
            variant="secondary"
            onClick={handleFetchPreview}
            loading={previewLoading}
            disabled={!percentage || previewLoading || selectedProductIds.length === 0}
          >
            Ver preview exacto
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!percentage || isLoading || serverPreview.will_update === 0}
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Confirmar y actualizar {serverPreview.will_update}
          </Button>
        )}
      </div>
    </div>
  );
}
